import { Button } from "./ui/button";
import { useProfile } from "@/context/ProfileContext";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Candidate } from "@/types/candidate";
import { getDocumentUrl } from "./document-buttons";
import { storage } from "@/config/firebaseConfig";
import { ref, listAll } from "firebase/storage";

interface TeamtailorCandidateButtonProps {
  candidate: Candidate;
}

// Helper function to check if CV is in valid format
const isCvValidFormat = async (uid: string): Promise<boolean> => {
  try {
    const folderRef = ref(storage, `mobileUsersFiles/${uid}/cv/`);
    const files = await listAll(folderRef);

    if (files.items.length === 0) {
      return false;
    }

    const hasValidCv = files.items.some((file) => {
      const fileName = file.name.toLowerCase();
      return fileName.endsWith(".pdf") || fileName.endsWith(".docx");
    });
    return hasValidCv;
  } catch (error) {
    console.error("Error checking CV format:", error);
    return false;
  }
};

export default function TeamtailorCandidateButton({
  candidate,
}: TeamtailorCandidateButtonProps) {
  const { businessProfile } = useProfile();
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const sendToTeamtailor = async () => {
    if (!businessProfile?.integrations?.teamtailor?.apiKey) {
      toast({
        title: "Fel",
        description:
          "Ingen Teamtailor API-nyckel konfigurerad. Vänligen lägg till en i Integrationer-inställningar.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const candidateData: any = {
        data: {
          type: "candidates",
          attributes: {
            "first-name": candidate.realFirebaseUserData?.givenName,
            "last-name": candidate.realFirebaseUserData?.surname,
            email: candidate.realFirebaseUserData?.email,
            phone: candidate.realFirebaseUserData?.phoneNumber || "",
            tags: "graftus",
          },
        },
      };

      // Only add resume if candidate has CV and it's in valid format
      if (candidate.realFirebaseUserData?.hasCv) {
        const isValidFormat = await isCvValidFormat(candidate.uid);
        if (isValidFormat) {
          candidateData.data.attributes.resume = await getDocumentUrl(
            candidate.uid,
            "cv"
          );
        }
      }

      const response = await fetch("https://api.teamtailor.com/v1/candidates", {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          Authorization: `Token token=${businessProfile.integrations.teamtailor.apiKey}`,
          "X-Api-Version": "20240404",
        },
        body: JSON.stringify(candidateData),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error(data);

        // Check if it's a duplicate email error
        if (
          response.status === 422 &&
          data.errors &&
          Array.isArray(data.errors)
        ) {
          const emailError = data.errors.find(
            (error: any) =>
              error.source?.pointer === "/data/attributes/email" &&
              error.detail?.includes("has already been taken")
          );

          if (emailError) {
            toast({
              title: "Kandidat finns redan",
              description: "Kandidaten har redan skickats till Teamtailor.",
              variant: "destructive",
            });
            setIsLoading(false);
            return;
          }

          // Check if it's an invalid CV file format error
          const cvFormatError = data.errors.find(
            (error: any) =>
              (error.source?.pointer === "/data/attributes/resume" ||
                error.source?.pointer ===
                  "/data/attributes/resume-file-name") &&
              error.detail?.includes("invalid file")
          );

          if (cvFormatError) {
            toast({
              title: "Felaktigt CV-format",
              description:
                "CV:t måste vara i formatet PDF eller DOCX för att skickas till Teamtailor.",
              variant: "destructive",
            });
            setIsLoading(false);
            return;
          }
        }

        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast({
        description: "Kandidaten har skickats till Teamtailor",
      });
    } catch (error) {
      console.error("Fel vid sändning till Teamtailor:", error);
      toast({
        title: "Fel",
        description:
          "Det gick inte att skicka kandidaten till Teamtailor. Försök igen.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={sendToTeamtailor}
      variant="ghost"
      className="hover:bg-blue-100 transition-colors"
      disabled={isLoading || !businessProfile?.integrations?.teamtailor?.apiKey}
      title={isLoading ? "Skickar..." : "Skicka till Teamtailor"}>
      <img
        src="https://www.google.com/s2/favicons?domain=teamtailor.com&sz=32"
        alt="Teamtailor"
        className="h-5 w-5"
      />
      {isLoading ? "Skickar..." : "Skicka till Teamtailor"}
    </Button>
  );
}
